import React, { useState } from "react";
import { useFieldArray, Control, useWatch } from "react-hook-form";
import { Plus, Trash2, X, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormFieldType } from "@api/shared-runtime/enums";
import type { OrgFormFormValues } from "./org-form-form";

const FIELD_TYPE_LABELS: Record<FormFieldType, string> = {
  [FormFieldType.Text]: "Short Text",
  [FormFieldType.Textarea]: "Long Text",
  [FormFieldType.Select]: "Single Select",
  [FormFieldType.MultiSelect]: "Multi Select",
  [FormFieldType.FileUpload]: "File Upload",
  [FormFieldType.Date]: "Date",
  [FormFieldType.Checkbox]: "Checkbox",
  [FormFieldType.Signature]: "Signature",
};

function getFieldTypeBadgeClass(fieldType: FormFieldType): string {
  switch (fieldType) {
    case FormFieldType.Select:
    case FormFieldType.MultiSelect:
      return "bg-blue-50 text-blue-700 border-blue-200";
    case FormFieldType.FileUpload:
      return "bg-indigo-50 text-indigo-700 border-indigo-200";
    case FormFieldType.Date:
      return "bg-amber-50 text-amber-700 border-amber-200";
    case FormFieldType.Checkbox:
      return "bg-green-50 text-green-700 border-green-200";
    case FormFieldType.Signature:
      return "bg-purple-50 text-purple-700 border-purple-200";
    default:
      return "bg-gray-50 text-gray-600 border-gray-200";
  }
}

interface FormFieldEditorProps {
  control: Control<OrgFormFormValues>;
  fieldIndex: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  isFirst: boolean;
  isLast: boolean;
  isNew?: boolean;
}

function OptionsEditor({
  control,
  fieldIndex,
}: {
  control: Control<OrgFormFormValues>;
  fieldIndex: number;
}) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `fields.${fieldIndex}.options` as never,
  });

  return (
    <div className="space-y-2">
      <Label className="text-xs text-gray-600">Options</Label>
      <div className="space-y-1.5">
        {fields.map((optField, optIndex) => (
          <FormField
            key={optField.id}
            control={control}
            name={`fields.${fieldIndex}.options.${optIndex}` as never}
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-1.5">
                  <FormControl>
                    <Input
                      placeholder={`Option ${optIndex + 1}`}
                      className="h-8 py-1 text-sm"
                      {...(field as React.InputHTMLAttributes<HTMLInputElement>)}
                    />
                  </FormControl>
                  <button
                    type="button"
                    onClick={() => remove(optIndex)}
                    className="text-red-400 hover:text-red-600 shrink-0"
                    aria-label="Remove option"
                  >
                    <X size={14} />
                  </button>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 text-xs border-dashed"
        onClick={() => append("" as never)}
      >
        <Plus size={12} className="mr-1" />
        Add Option
      </Button>
    </div>
  );
}

export function FormFieldEditor({
  control,
  fieldIndex,
  onMoveUp,
  onMoveDown,
  onRemove,
  isFirst,
  isLast,
  isNew = false,
}: FormFieldEditorProps) {
  const [isExpanded, setIsExpanded] = useState(isNew);

  const fieldTypeValue = useWatch({
    control,
    name: `fields.${fieldIndex}.fieldType`,
  });

  const labelValue = useWatch({
    control,
    name: `fields.${fieldIndex}.label`,
  });

  const isRequiredValue = useWatch({
    control,
    name: `fields.${fieldIndex}.isRequired`,
  });

  const showOptions =
    fieldTypeValue === FormFieldType.Select ||
    fieldTypeValue === FormFieldType.MultiSelect;

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      {/* Compact summary row */}
      <div className="flex items-center gap-1.5 sm:gap-2 rounded-md border border-gray-100 bg-gray-50 px-1.5 sm:px-2 py-1.5 group hover:border-gray-200 transition-colors">
        {/* Field number */}
        <span className="shrink-0 text-xs font-mono text-gray-400 w-4 text-center">
          {fieldIndex + 1}
        </span>

        {/* Label — click to expand */}
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex-1 min-w-0 text-left flex items-center gap-1.5"
            aria-label={isExpanded ? "Collapse field" : "Expand field to edit"}
          >
            {isExpanded ? (
              <ChevronDown
                size={12}
                className="shrink-0 text-gray-400 transition-transform"
              />
            ) : (
              <ChevronRight
                size={12}
                className="shrink-0 text-gray-400 transition-transform"
              />
            )}
            <span
              className={`text-sm truncate ${labelValue ? "text-gray-800" : "text-gray-400 italic"}`}
            >
              {labelValue || `Field ${fieldIndex + 1} — click to edit`}
            </span>
          </button>
        </CollapsibleTrigger>

        {/* Compact badges */}
        <div className="flex items-center gap-1 shrink-0">
          {fieldTypeValue && (
            <Badge
              variant="outline"
              className={`text-xs h-5 px-1.5 ${getFieldTypeBadgeClass(fieldTypeValue)}`}
            >
              {FIELD_TYPE_LABELS[fieldTypeValue]}
            </Badge>
          )}
          {isRequiredValue ? (
            <span className="text-xs text-gray-500 font-medium">req</span>
          ) : (
            <span className="text-xs text-gray-400 italic">opt</span>
          )}
        </div>

        {/* Reorder controls */}
        <div className="flex gap-0.5 shrink-0 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-25 disabled:cursor-not-allowed p-1.5 rounded hover:bg-gray-200 transition-colors"
            aria-label="Move field up"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="2,8 6,4 10,8" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-25 disabled:cursor-not-allowed p-1.5 rounded hover:bg-gray-200 transition-colors"
            aria-label="Move field down"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="2,4 6,8 10,4" />
            </svg>
          </button>
        </div>

        {/* Remove */}
        <button
          type="button"
          onClick={onRemove}
          className="text-red-300 hover:text-red-500 shrink-0 p-1.5 rounded hover:bg-red-50 transition-colors sm:opacity-0 sm:group-hover:opacity-100"
          aria-label="Remove field"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {/* Expanded detail panel */}
      <CollapsibleContent>
        <div className="border border-t-0 border-gray-100 rounded-b-md bg-white px-3 py-3 space-y-3">
          {/* Label */}
          <FormField
            control={control}
            name={`fields.${fieldIndex}.label`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs text-gray-600">Label</FormLabel>
                <FormControl>
                  <Input
                    placeholder={`Field ${fieldIndex + 1} label`}
                    className="h-8 py-1 text-sm"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Description */}
          <FormField
            control={control}
            name={`fields.${fieldIndex}.description`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs text-gray-600">
                  Description (optional)
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Description for this field..."
                    className="h-8 py-1 text-sm"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Field type + required row */}
          <div className="flex flex-wrap gap-3">
            <FormField
              control={control}
              name={`fields.${fieldIndex}.fieldType`}
              render={({ field }) => (
                <FormItem className="flex-1 min-w-[160px]">
                  <FormLabel className="text-xs text-gray-600">
                    Field Type
                  </FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="h-8 py-1 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(FormFieldType)
                        // Signature fields are created server-side by PDF sync —
                        // orgs cannot add them manually.
                        .filter((type) => type !== FormFieldType.Signature)
                        .map((type) => (
                          <SelectItem key={type} value={type}>
                            {FIELD_TYPE_LABELS[type]}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name={`fields.${fieldIndex}.isRequired`}
              render={({ field }) => (
                <FormItem className="flex items-end gap-2 pb-0.5">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="scale-90"
                    />
                  </FormControl>
                  <FormLabel className="text-xs text-gray-600 mb-0">
                    Required
                  </FormLabel>
                </FormItem>
              )}
            />
          </div>

          {fieldTypeValue === FormFieldType.Checkbox && isRequiredValue && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
              <p className="text-xs text-amber-800">
                <span className="font-semibold">Note:</span> The provider will
                not be able to submit unless this checkbox is checked. For a
                yes/no question, use a Single Select with "Yes" and "No" options
                instead.
              </p>
            </div>
          )}

          {showOptions && (
            <OptionsEditor control={control} fieldIndex={fieldIndex} />
          )}

          {/* Delete button inside expanded view */}
          <div className="pt-2 border-t border-gray-100 mt-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 px-3 text-xs"
              onClick={onRemove}
            >
              <Trash2 size={14} className="mr-1.5" />
              Delete Field
            </Button>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
